<template>
  <div class="node-list-wrap">
    <div v-if="isInitialized"
         class="node-list"
         :class="{ 'thru-highlighting': thruDataArray }"
         :style="{ height: listHeight + 'px' }">
      <template v-if="useCanvasToDrawPath">
        <template v-for="(sequence, sequenceIndex) in nodesDataArray">
          <ba-trend-path-canvas v-if="sequenceIndex < nodesDataArray.length - 1 && isPathGroupVisible(sequenceIndex)"
                                :key="'path-canvas-' + sequenceIndex"
                                :nodesDataArray="nodesDataArray"
                                :left="getPathGroupX(sequenceIndex)"
                                :top="nodeBaseTop"
                                :width="nodeGap + nodeItemBorderRadius * 2"
                                :height="listHeight - nodeBaseTop - nodeBottomMargin + 2"
                                :connectionStrength="connectionStrength"
                                :paneScale="paneScale"
                                :panePosition="panePosition"
                                :highlightedSequenceAndNodeIndex="thruData ? thruData._sequenceIndex + '-' + thruData._nodeIndex : ''"
                                @onLineClick="onLineClick"
                                @onCloseInfoLayer="onCloseInfoLayer">
            <template v-for="(nodeData, nodeIndex) in sequence.nodesArray">
              <template v-if="nodeData.nextArray">
                <template v-for="(nextData, nextIndex) in nodeData.nextArray">
                  <ba-trend-path-canvas-item :key="'path-' + sequenceIndex + '-' + nodeIndex + '-' + nextIndex"
                                             :id="'path-' + sequenceIndex + '-' + nodeIndex + '-' + getPathNextIndex(sequenceIndex, nextData._id)"
                                             :lineInfo="nextData._lineInfo"
                                             :nodeItemBorderRadius="nodeItemBorderRadius"
                                             :isInterrupted="!isPathFullVisible(nextData)"
                                             :isActive="isPathActive(sequenceIndex, nodeIndex, getPathNextIndex(sequenceIndex, nextData._id))"
                                             :hasThruPath="hasThruPath(sequenceIndex, nodeData._id, nextData._id)" />
                  <template v-if="hasThruPath(sequenceIndex, nodeData._id, nextData._id)">
                    <ba-trend-path-canvas-item :key="'thrupath-' + sequenceIndex + '-' + nodeIndex + '-' + nextIndex"
                                               :id="'thrupath-' + sequenceIndex + '-' + nodeIndex + '-' + getPathNextIndex(sequenceIndex, nextData._id)"
                                               :isThruPath="true"
                                               :nodeItemBorderRadius="nodeItemBorderRadius"
                                               :lineInfo="thruData.seqMap[sequenceIndex].nodes[nodeData._id].next[nextData._id]._lineInfo" />
                  </template>
                </template>
              </template>
            </template>
          </ba-trend-path-canvas>
        </template>
      </template>
      <template v-else
                v-for="(sequence, sequenceIndex) in nodesDataArray">
        <svg v-if="sequenceIndex < nodesDataArray.length - 1 && isPathGroupVisible(sequenceIndex)"
             :key="'path-svg-' + sequenceIndex"
             class="trend-paths"
             :style="{
                left: getPathGroupX(sequenceIndex) + 'px',
                top: nodeBaseTop + 'px',
                width: nodeGap + nodeItemBorderRadius * 2 + 'px',
                height: (listHeight - nodeBaseTop - nodeBottomMargin + 2) + 'px'
              }">
          <template v-for="(nodeData, nodeIndex) in sequence.nodesArray">
            <template v-if="nodeData.nextArray">
              <template v-for="(nextData) in nodeData.nextArray">
                <g v-if="isPathFullVisible(nextData)"
                   :key="sequenceIndex + '-' + nodeIndex + '-' + getPathNextIndex(sequenceIndex, nextData._id)"
                   :data-id="sequenceIndex + '-' + nodeIndex + '-' + getPathNextIndex(sequenceIndex, nextData._id)"
                   class="path-group"
                   :class="{ active: isPathActive(sequenceIndex, nodeIndex, getPathNextIndex(sequenceIndex, nextData._id)) }"
                   @click="(e) => onLineClick(e, sequenceIndex, nodeIndex, getPathNextIndex(sequenceIndex, nextData._id), hasThruPath(sequenceIndex, nodeData._id, nextData._id))"
                >
                  <ba-node-path class="default-path"
                                :lineInfo="nextData._lineInfo"
                                :nodeItemBorderRadius="nodeItemBorderRadius" />
                  <ba-node-path v-if="Math.max(nextData._lineInfo.widthOut, nextData._lineInfo.widthIn) < 10"
                                class="hoverable-path"
                                :lineInfo="nextData._lineInfo"
                                :nodeItemBorderRadius="nodeItemBorderRadius" />
                  <template v-if="hasThruPath(sequenceIndex, nodeData._id, nextData._id)">
                    <ba-node-path class="thru-path"
                                  :lineInfo="thruData.seqMap[sequenceIndex].nodes[nodeData._id].next[nextData._id]._lineInfo"
                                  :nodeItemBorderRadius="nodeItemBorderRadius" />
                  </template>
                </g>
                <g v-else
                   class="path-group interrupted-group"
                   :class="{ active: isPathActive(sequenceIndex, nodeIndex, getPathNextIndex(sequenceIndex, nextData._id)) }"
                   :key="sequenceIndex + '-' + nodeIndex + '-' + getPathNextIndex(sequenceIndex, nextData._id)"
                   :data-id="sequenceIndex + '-' + nodeIndex + '-' + getPathNextIndex(sequenceIndex, nextData._id)"
                   @click="(e) => onLineClick(e, sequenceIndex, nodeIndex, getPathNextIndex(sequenceIndex, nextData._id), hasThruPath(sequenceIndex, nodeData._id, nextData._id))"
                >
                  <ba-node-path class="default-path"
                                :lineInfo="nextData._lineInfo"
                                :nodeItemBorderRadius="nodeItemBorderRadius" />
                  <ba-node-path v-if="Math.max(nextData._lineInfo.widthOut, nextData._lineInfo.widthIn) < 10"
                                class="hoverable-path"
                                :lineInfo="nextData._lineInfo"
                                :nodeItemBorderRadius="nodeItemBorderRadius" />
                </g>
              </template>
            </template>
          </template>
        </svg>
      </template>
      <svg class="trend-path-defs">
        <defs>
          <linearGradient x1="47.6953629%" y1="97.6798935%" x2="48.5112725%" y2="4.88868507%" id="dropped-line-linear-gradient">
            <stop stop-color="#FACBA1" stop-opacity="0" offset="0%"></stop>
            <stop stop-color="#FAC195" offset="29.1237058%"></stop>
            <stop stop-color="#FD5C1A" offset="100%"></stop>
          </linearGradient>
        </defs>
      </svg>
      <svg v-if="!useCanvasToDrawPath" class="trend-path-defs">
        <defs>
          <linearGradient x1="0%" y1="0%" x2="100%" y2="0%" id="interrupted-line-linear-gradient">
            <stop stop-color="#b8c008" offset="8%"></stop>
            <stop stop-color="#b8c008" stop-opacity="0" offset="13%"></stop>
            <stop stop-color="#b8c008" stop-opacity="0" offset="87%"></stop>
            <stop stop-color="#b8c008" offset="92%"></stop>
          </linearGradient>
          <mask id="interrupted-line-linear-gradient-mask">
            <rect :x="0"
                  :y="0"
                  :width="nodeGap + nodeItemBorderRadius * 2"
                  :height="listHeight - nodeBaseTop - nodeBottomMargin + 2"
                  fill="url(#interrupted-line-linear-gradient)" />
          </mask>
          <clipPath id="interrupted-line-linear-gradient-clip-path">
            <rect :x="0"
                  :y="0"
                  :width="(nodeGap + nodeItemBorderRadius * 2) * 0.13"
                  :height="listHeight - nodeBaseTop - nodeBottomMargin + 2"
                  fill="black" />
            <rect :x="(nodeGap + nodeItemBorderRadius * 2) - ((nodeGap + nodeItemBorderRadius * 2) * 0.13)"
                  :y="0"
                  :width="(nodeGap + nodeItemBorderRadius * 2) * 0.13"
                  :height="listHeight - nodeBaseTop - nodeBottomMargin + 2"
                  fill="black" />
          </clipPath>
        </defs>
      </svg>
      <div class="column" v-for="(sequence, sequenceIndex) in nodesDataArray"
                          :key="sequenceIndex"
                          :style="{
                            width: nodeWidth + 'px',
                            marginLeft: sequenceIndex > 0 ? nodeGap + 'px' : ''
                          }">
        <div class="col-info-header" v-if="sequenceIndex === 0">
          <div :class="['match-type', journeyOptions.matchType]"
            v-btf-tooltip:top.start="{contents: journeyOptions.matchType === 'exact' ? $t('common.matchType.exactGuide') : $t('common.matchType.simpleGuide'), size: 220}"
            >
            {{ journeyOptions.matchType}}
          </div>
          <div class="custom-button" v-if="isCustomNodeAvailable">
              <span class="label">
                        {{ $t('customTag.label') }}
                      </span>
            <ba-switch :value="isCustomNode"
                      :default="isCustomNode"
                      :width="44"
                      :size="'small'"
                      :disabled="isCustomDisabled"
                      v-btf-tooltip:top.start="{contents: isCustomDisabled ? $t('customTag.disabled', { date: customRegDate }) : ''}"
                      @change="onCustomTag"  />
          </div>
          <div class="category-header">
            <template v-if="domainCategory && domainSubCategory && !isCustomNode">
              <div class="category-icon large">
                <i :class="['icon', DOMAIN_CATEGORY_ICON[domainCategory.key], 'size-24']"></i>
              </div>
              <div class="dropdown">
                <div class="selected" @click="$emit('onShowCategorySelector')">
                  <span class="label">{{ domainCategory.subcategories[domainSubCategory].title[$i18n.locale] }}</span>
                  <i class="icon icon--arrow-updown"></i>
                </div>
              </div>
            </template>
            <span v-if="domain.matchType === 'exact'">{{ $t( `benchmark.exactTitle`) }}</span>
            <span v-else-if="isCustomNode">{{ $t( `benchmark.customTagTitle`) }}</span>
            <span v-else-if="domainSubCategory && benchmarkData && benchmarkData[0]">{{ $t( `benchmark.categoryTitle`) }}</span>
            <span v-else-if="domainSubCategory">{{ $t( `benchmark.noDataTitle`) }}</span>
            <span v-else>{{ $t( `benchmark.defaultTitle`) }}</span>
            <span class="ai-robot-burrow"></span>
          </div>
        </div>
        <div v-if="isSequenceInfoVisible(sequenceIndex)"
             class="info"
             :style="{
               left: sequenceIndex * (nodeWidth + nodeGap) + 'px',
               width: nodeWidth + 'px'
             }">
          <strong class="sequence">{{ sequenceIndex + 1 }}</strong>
          <span class="session" :class="{'invalid-value': sequence.seqSessCntIsInaccurate}">
            <i class="icon icon--ba-session"></i>
            {{ sequence.seqSessCntIsInaccurate ? $t('common.invalidValue') : sequence.seqSessCnt | letterThousandMillion(0) }}
          </span>
          <span class="dropoff">
            <i class="icon icon--dropoff-1"></i>
            <span>{{ ((sequence.dropCnt / sequence.sampleSeqSessCnt) * 100) | fixedInt(2, 1) }}%</span>
            <template v-if="!isCustomNode && domainCategory && domainSubCategory && benchmarkData && benchmarkData[sequenceIndex]">
              <span :class="`icon--benchmark-${getHighLow(sequenceIndex)} size-16`"></span>
              <span class="benchmark-item" @mouseenter="showTooltip = sequenceIndex" @mouseleave="showTooltip = null">{{ (benchmarkData[sequenceIndex] * 100) | fixedInt(2, 1)}}%
                <span v-if="showTooltip === sequenceIndex" class="tooltip-category bottom">
                  <i :class="`icon ${DOMAIN_CATEGORY_ICON[domainCategory.key]}-blue size-14`"></i>
                  <span>{{ domainCategory.subcategories[domainSubCategory].title[$i18n.locale] }}</span>
                </span>
              </span>
            </template>
          </span>
          <ba-tracking-funnel v-if="isTrackable && sequenceIndex === 0" :tracking="tracking" :overallSessionData="overallSessionData" :sessionRatio="sessionRatio" :isTrackingAll="isTrackingAll" @setTracking="setTracking" />
          <ai-referrer-summary
            v-if="sequenceIndex === 0"
            class="trend-ai-referrer-summary"
            :class="{ 'is-below-tracking-funnel': isTrackable }"
          />
          <ba-tracking-label
            v-if="isTrackable && sequenceIndex === 0"
            :hover-text="trackingLabelText"
            :ratio="sessionRatio.selectedMember + sessionRatio.selectedGuest"
            :is-bounce="true"
            @click="$emit('onShowTrackingLayer', 0, -1)" />
        </div>
        <template v-for="(nodeData, nodeIndex) in sequence.nodesArray">
          <ba-node-item v-if="isNodeVisible(nodeData._layout)"
                        :key="sequenceIndex + '-' + nodeIndex"
                        :id="sequenceIndex + '-' + nodeIndex"
                        :sequenceIndex="sequenceIndex"
                        :nodeIndex="nodeIndex"
                        :nodeData="nodeData"
                        :thruData="getThruData(sequenceIndex, nodeData._id)"
                        :nodeInfo="nodesData.nodeMap[nodeData._id]"
                        :layout="nodeData._layout"
                        :nodeBaseTop="nodeBaseTop"
                        :nodeBottomMargin="nodeBottomMargin"
                        :isTrackable="isTrackable"
                        :tracking="tracking"
                        :grouping="grouping"
                        :group="group"
                        :visibleOptions="visibleOptions"
                        :isActive="(contextMenuVisible || reportShowing) && (focusedSequenceIndex === sequenceIndex && focusedNodeIndex === nodeIndex)"
                        :isFocused="(
                          (!thruDataArray && nodeReporting) ||
                          (!!thruDataArray &&
                            (nodeReporting && isNodeFocused(sequenceIndex, nodeIndex, nodeData)) ||
                            (reportShowing && !isNodeFocused(sequenceIndex, nodeIndex, nodeData))
                          )
                        ) && focusedSequenceIndex === sequenceIndex && focusedNodeIndex === nodeIndex"
                        :isDimmed="!!thruDataArray && !isNodeFocused(sequenceIndex, nodeIndex, nodeData)"
                        :isHighlighted="!!thruDataArray && highlightedSequenceIndex === sequenceIndex && highlightedNodeIndex === nodeIndex"
                        :isThruHighlighting="!!thruDataArray"
                        :isCustomNode="isCustomNode"
                        :overallSessionData="overallSessionData"
                        @onNodeAreaClick="onNodeAreaClick"
                        @onShowTrackingLayer="$emit('onShowTrackingLayer', sequenceIndex, nodeIndex)" />
        </template>
        <div
          class="more"
          v-if="sequenceIndex + 1 === nodesDataArray.length && sequenceIndex !== maxSequence"
          :style="{ left: sequenceIndex * (nodeWidth + nodeGap) + nodeWidth + 98 + 'px' }"
        >
          <button class="more-x" @click="onExpandButtonClick">
            <i class="icon icon--plus-blue"></i>
          </button>
          <span class="more-text">More</span>
        </div>
      </div>
      <ba-node-context-menu :left="contextMenuLeft"
                            :top="contextMenuTop"
                            :paneScale="paneScale"
                            :visible="contextMenuVisible"
                            :focusedSequenceIndex="focusedSequenceIndex"
                            :focusedNodeIndex="focusedNodeIndex"
                            :highlightedSequenceIndex="highlightedSequenceIndex"
                            :highlightedNodeIndex="highlightedNodeIndex"
                            @onHighlightNodeClick="onHighlightNodeClick"
                            @onRemoveHighlightClick="onRemoveHighlightClick"
                            @onNodeReportClick="onNodeReportClick"
                            @onCloseContextMenu="onCloseContextMenu" />
      <ba-node-info-layer :left="infoLayerLeft"
                          :top="infoLayerTop"
                          :visible="infoLayerVisible"
                          :nodesDataArray="nodesDataArray"
                          :thruData="thruData"
                          :nodeMap="nodesData.nodeMap"
                          :grouping="grouping"
                          :group="group"
                          :paneScale="paneScale"
                          :focusedSequenceIndex="focusedSequenceIndex"
                          :focusedNodeIndex="focusedNodeIndex"
                          :focusedNextIndex="focusedNextIndex"
                          :isThruPath="focusedLineIsThruPath"
                          @onCloseInfoLayer="onCloseInfoLayer" />
    </div>
    <transition name="fade">
      <div v-if="reportShowing"
           class="dimmer report" />
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { EventBus } from '@/libs/event-bus'
import { thousand } from '@/libs/utils'
import { DOMAIN_CATEGORY_ICON } from '@/libs/constants'

import BaNodeItem from '@/components/trend/NodeItem'
import BaNodePath from '@/components/trend/NodePath'
import BaTrendPathCanvas from '@/components/trend/NodePathCanvas'
import BaTrendPathCanvasItem from '@/components/trend/NodePathCanvasItem'
import BaNodeContextMenu from '@/components/trend/NodeContextMenu'
import BaNodeInfoLayer from '@/components/trend/NodeInfoLayer'
import BaSwitch from '@/components/ui/ba/Switch.vue'
import BaTrackingLabel from '@/components/journey/TrackingLabel.vue'
import BaTrackingFunnel from '@/components/journey/TrackingFunnel.vue'
import AiReferrerSummary from '@/components/report/AiReferrerSummary.vue'
export default {
  name: 'BaNodeList',
  components: {
    AiReferrerSummary,
    BaSwitch,
    BaNodeItem,
    BaNodePath,
    BaTrendPathCanvas,
    BaTrendPathCanvasItem,
    BaNodeContextMenu,
    BaNodeInfoLayer,
    BaTrackingLabel,
    BaTrackingFunnel
  },
  props: {
    benchmarkData: {
      type: Object,
      default: null
    },
    nodesData: {
      type: Object,
      required: true
    },
    nodesDataArray: {
      type: Array,
      required: true
    },
    thruData: {
      type: Object
    },
    thruDataArray: {
      type: Array
    },
    moreDataIndex: {
      type: Number,
      required: true
    },

    paneScale: {
      type: Number,
      required: true
    },
    paneWrapSize: {
      type: Array,
      required: true
    },
    panePosition: {
      type: Array,
      required: true
    },

    nodeControlWidth: {
      type: Number,
      required: true
    },
    nodeControlHeight: {
      type: Number,
      required: true
    },
    sizeControlMin: {
      type: Number,
      required: true
    },
    sizeControlMax: {
      type: Number,
      required: true
    },

    panePadding: {
      type: Array,
      default: () => [0, 0, 0, 0]
    },
    paneExtraPadding: {
      type: Array,
      default: () => [0, 0, 0, 0]
    },
    nodeBaseTop: {
      type: Number,
      default: 95
    },
    nodeMinWidth: {
      type: Number,
      default: 200
    },
    nodeMaxWidth: {
      type: Number,
      default: 285
    },
    nodeMinHeight: {
      type: Number,
      default: 3
    },
    firstNodeMinHeight: {
      type: Number,
      default: 100
    },
    firstNodeMaxHeight: {
      type: Number,
      default: 1000
    },
    nodeMinGap: {
      type: Number,
      default: 130
    },
    nodeMaxGap: {
      type: Number,
      default: 230
    },
    connectionStrength: {
      type: Number,
      required: true
    },
    reportShowing: {
      type: Boolean,
      required: true
    },
    nodeReporting: {
      type: Boolean,
      required: true
    },
    isTrackable: {
      default: false
    },
    tracking: {
      default: false
    },
    grouping: {
      type: Boolean,
      required: true
    },
    group: {
      type: String,
      required: true
    },
    visibleOptions: {
      type: Object,
      required: true
    },
    domainSubCategory: {
      type: String,
      default: null
    },
    domainCategory: {
      type: Object,
      default: null
    },
    isCustomNode: {
      type: Boolean,
      default: false
    },
    isCustomNodeAvailable: {
      type: Boolean,
      default: false
    },
    isCustomDisabled: {
      type: Boolean,
      default: false
    },
    customRegDate: {
      type: String,
      default: null
    },
    overallSessionData: {
      type: Object,
      default: () => ({
        memberSessCnt: 0,
        sessCnt: 0
      })
    }
  },
  data () {
    return {
      isInitialized: false,
      listHeight: 0,

      nodeItemBorderRadius: 8,

      focusedSequenceIndex: -1,
      focusedNodeIndex: -1,
      focusedNextIndex: -1,
      focusedLineIsThruPath: false,

      highlightedSequenceIndex: -1,
      highlightedNodeIndex: -1,

      contextMenuLeft: 0,
      contextMenuTop: 0,
      contextMenuVisible: false,

      infoLayerLeft: 0,
      infoLayerTop: 0,
      infoLayerVisible: false,

      numMaxSequences: 20,
      numMaxFullSequences: 20,

      thruDataArrayCopy: null,

      useCanvasToDrawPath: location.href.indexOf('canvas=1') > -1,
      showTooltip: null,
      DOMAIN_CATEGORY_ICON
    }
  },
  computed: {
    ...mapState([
      'domain',
      'journeyOptions',
      'journeyPeriod',
      'originTrendData'
    ]),
    nodeWidth () {
      return Math.ceil(this.nodeMinWidth + (this.nodeMaxWidth - this.nodeMinWidth) * (this.nodeControlWidth / (this.sizeControlMax - this.sizeControlMin)))
    },
    nodeGap () {
      return Math.ceil(this.nodeMinGap + (this.nodeMaxGap - this.nodeMinGap) * (this.nodeControlWidth / (this.sizeControlMax - this.sizeControlMin)))
    },
    panePaddingTop () {
      return this.panePadding[0] + this.paneExtraPadding[0]
    },
    panePaddingBottom () {
      return this.panePadding[2] + this.paneExtraPadding[2]
    },
    panePaddingLeft () {
      return this.panePadding[3] + this.paneExtraPadding[3]
    },
    nodeBottomMargin () {
      return this.isTrackable ? 105 : 100
    },
    unscaledPanePosition () {
      const reversedScale = 1 / this.paneScale
      return [
        this.panePosition[0] * reversedScale,
        this.panePosition[1] * reversedScale
      ]
    },
    maxSequence () {
      return Math.max.apply(null, Object.keys(this.nodesData.seqMap))
    },
    sessionRatio () {
      const { memberSessCnt, sessCnt } = this.overallSessionData
      const allMember = memberSessCnt / sessCnt
      const allGuest = 1 - allMember

      return {
        allMember,
        allGuest,
        selectedMember: this.tracking ? this.tracking.type === 'member' ? this.nodesDataArray[0].memberSessCnt / sessCnt : 0 : allMember,
        selectedGuest: this.tracking ? this.tracking.type === 'guest' ? (this.nodesDataArray[0].seqSessCnt - this.nodesDataArray[0].memberSessCnt) / sessCnt : 0 : allGuest
      }
    },
    isTrackingAll () {
      if (!this.tracking) return false
      if (this.tracking.type === 'member') {
        return this.overallSessionData.memberSessCnt === this.nodesDataArray[0].seqSessCnt
      } else {
        return this.overallSessionData.sessCnt - this.overallSessionData.memberSessCnt === this.nodesDataArray[0].seqSessCnt
      }
    },
    trackingLabelText () {
      let label = this.$t('tracking.tracking')
      if (!this.tracking) return label

      if (this.tracking.type === 'guest') {
        label += ` ${this.$t('tracking.guest')}`
      } else {
        label += ` ${this.$t('tracking.member')}`

        if (this.nodesDataArray[0].memberCnt === 1) {
          label += ` ${this.tracking.selectedMembers[0].displayId}`
        } else {
          label += ` ${thousand(this.nodesDataArray[0].memberCnt)}`
        }
      }
      return label
    }
  },
  watch: {
    async nodesDataArray (data) {
      if (data) {
        this.isInitialized = false
        this.listHeight = 0
        // await this.getBenchmarkData()
        this.onCloseContextMenu()
        this.onRemoveHighlightClick()
        this.onCloseInfoLayer()
        this.getMaxSequences()
        this.calculateNodeSize()
      }
    },
    thruDataArray (data) {
      if (data) {
        this.thruDataArrayCopy = this.thruDataArray
        const firstNode = this.nodesDataArray[0].nodesArray[0]
        this.calculateLineInfo(firstNode._layout.height, firstNode.sessCnt, this.thruDataArray)
      }
    },
    moreDataIndex (index) {
      this.calculateNodeSize(index)
    },
    nodeControlWidth (value) {
      this.calculateNodeSize()
    },
    nodeControlHeight (value) {
      this.listHeight = 0
      this.calculateNodeSize()
    }
  },
  async created () {
    // await this.getBenchmarkData()
  },
  async mounted () {
    this.getMaxSequences()
    this.calculateNodeSize()
  },
  methods: {
    onCustomTag () {
      this.$emit('onCustomTag')
    },
    getPathGroupX (index) {
      return index * (this.nodeWidth + this.nodeGap) + this.nodeWidth - this.nodeItemBorderRadius
    },
    onMoreButtonClick () {
      this.$emit('onLoadMoreData')
    },
    onExpandButtonClick () {
      this.$emit('expandSequence')
    },
    onNodeAreaClick (e, sequenceIndex, nodeIndex) {
      if (this.nodesDataArray[sequenceIndex].nodesArray[nodeIndex]._id === '-1') {
        this.focusedSequenceIndex = sequenceIndex
        this.focusedNodeIndex = nodeIndex
        this.onNodeReportClick()
      } else if (this.contextMenuVisible && this.isSameEventPoint(e)) {
        this.onCloseContextMenu()
      } else {
        const point = this.adjustEventPoint(this.getEventPoint(e))
        this.focusedSequenceIndex = sequenceIndex
        this.focusedNodeIndex = nodeIndex
        this.contextMenuLeft = point[0]
        this.contextMenuTop = point[1]
        this.contextMenuVisible = true
      }
    },
    onCloseContextMenu (onlyClose) {
      this.focusedSequenceIndex = this.focusedNodeIndex = -1
      this.contextMenuVisible = false
    },

    onHighlightNodeClick () {
      this.highlightedSequenceIndex = this.focusedSequenceIndex
      this.highlightedNodeIndex = this.focusedNodeIndex
      this.$emit('onHighlightNode', this.highlightedSequenceIndex, this.highlightedNodeIndex)
      this.onCloseContextMenu()
    },
    onRemoveHighlightClick () {
      this.$emit('onRemoveHighlightNode')
      this.highlightedSequenceIndex = this.highlightedNodeIndex = -1
      this.onCloseContextMenu()
    },
    onNodeReportClick () {
      this.$emit('onShowReport', this.focusedSequenceIndex, this.focusedNodeIndex, this.highlightedSequenceIndex, this.highlightedNodeIndex)
      this.contextMenuVisible = false
    },

    onLineClick (e, sequenceIndex, nodeIndex, nextIndex, isThruPath) {
      if (this.infoLayerVisible && this.isSameEventPoint(e)) {
        this.onCloseInfoLayer()
      } else {
        const point = this.adjustEventPoint(this.getEventPoint(e))
        this.focusedSequenceIndex = sequenceIndex
        this.focusedNodeIndex = nodeIndex
        this.focusedNextIndex = nextIndex
        this.focusedLineIsThruPath = isThruPath
        if (!this.focusedLineIsThruPath && this.thruDataArray) {
          const nodeData = this.nodesDataArray[sequenceIndex].nodesArray[nodeIndex]
          const nextId = this.nodesDataArray[sequenceIndex + 1].nodesArray[nextIndex]._id
          if (this.thruDataArray[sequenceIndex] && this.thruDataArray[sequenceIndex].nodes[nodeData._id] && this.thruDataArray[sequenceIndex + 1] && this.thruDataArray[sequenceIndex + 1].nodes[nextId]) {
            this.focusedLineIsThruPath = true
          }
        }
        this.infoLayerLeft = point[0]
        this.infoLayerTop = point[1]
        this.infoLayerVisible = true
      }
    },
    onCloseInfoLayer () {
      this.focusedSequenceIndex = this.focusedNodeIndex = this.focusedNextIndex = -1
      this.infoLayerVisible = false
    },

    onSettingUtm (tag) {
      EventBus.$emit('settingUtm', true, tag)
    },

    isVisible (left, right, top, bottom) {
      const [ paneWrapWidth, paneWrapHeight ] = this.paneWrapSize
      const limitLeft = this.nodeReporting ? -paneWrapWidth / 2 : 0
      const limitRight = this.nodeReporting ? paneWrapWidth * 1.5 : paneWrapWidth
      return !(limitRight < left * this.paneScale || right * this.paneScale < limitLeft || paneWrapHeight < top * this.paneScale || bottom * this.paneScale < 0)
    },
    isSequenceInfoVisible (sequenceIndex) {
      const [ paneX, paneY ] = this.unscaledPanePosition
      const left = (sequenceIndex * (this.nodeWidth + this.nodeGap)) + paneX + this.panePaddingLeft
      const right = left + this.nodeWidth
      const top = paneY + this.panePaddingTop
      const bottom = top + 70
      return this.isVisible(left, right, top, bottom)
    },
    isNodeVisible (layout) {
      const [ paneX, paneY ] = this.unscaledPanePosition
      const left = layout.left + paneX + this.panePaddingLeft
      const right = left + layout.width + 40
      const top = layout.top - 20 + paneY + this.nodeBaseTop + this.panePaddingTop
      const bottom = top + layout.height + this.nodeBottomMargin
      return this.isVisible(left, right, top, bottom)
    },
    isPathGroupVisible (sequenceIndex) {
      const left = this.getPathGroupX(sequenceIndex) + this.unscaledPanePosition[0] + this.panePaddingLeft
      const right = left + this.nodeGap + this.nodeItemBorderRadius * 2
      const top = 0
      const bottom = this.paneWrapSize[1]
      return this.isVisible(left, right, top, bottom)
    },
    isPathFullVisible (nextData) {
      if (this.thruData || Math.max(10, this.connectionStrength) >= nextData._rankRate) {
        return true
      }
      return false
    },
    isPathActive (sequenceIndex, nodeIndex, nextIndex) {
      return (this.infoLayerVisible && this.focusedSequenceIndex === sequenceIndex && this.focusedNodeIndex === nodeIndex && this.focusedNextIndex === nextIndex)
    },

    isNodeFocused (sequenceIndex, nodeIndex, nodeData) {
      if (this.thruDataArray || this.nodeReporting) {
        if (this.thruDataArrayCopy) {
          return !!(this.thruDataArrayCopy[sequenceIndex] && this.thruDataArrayCopy[sequenceIndex].nodes[nodeData._id])
        } else {
          return this.focusedSequenceIndex === sequenceIndex && this.focusedNodeIndex === nodeIndex
        }
      }
      return false
    },

    hasThruPath (sequenceIndex, nodeId, nextId) {
      return !!(this.thruDataArray && this.thruData.seqMap[sequenceIndex] && this.thruData.seqMap[sequenceIndex].nodes[nodeId] && this.thruData.seqMap[sequenceIndex].nodes[nodeId].next[nextId] && this.thruData.seqMap[sequenceIndex].nodes[nodeId].next[nextId]._lineInfo)
    },
    getThruData (sequenceIndex, nodeId) {
      return this.thruDataArray && this.thruDataArray[sequenceIndex] && this.thruDataArray[sequenceIndex].nodes[nodeId] ? this.thruDataArray[sequenceIndex].nodes[nodeId] : null
    },
    getPathNextIndex (sequenceIndex, nextId) {
      return this.nodesDataArray[sequenceIndex + 1].nodesArray.indexOf(this.nodesDataArray[sequenceIndex + 1].nodes[nextId])
    },

    getMaxSequences () {
      this.numMaxSequences = 20
      const trendMap = this.nodesData.trendMap
      for (let i = 0; i < this.numMaxFullSequences; i++) {
        if (!trendMap[i] || !trendMap[i].length) {
          this.numMaxSequences = i
          break
        }
      }
    },

    thousandValue (v) {
      return thousand(v)
    },
    calculateNodeSize (fromIndex) {
      const firstNodeSessionCount = this.nodesDataArray[0].nodesArray[0].sessCnt

      let firstNodeHeight
      if (!this.isInitialized) {
        const firstSequenceSessionCount = this.nodesDataArray[0].seqSessCnt
        let boxAreasHeight = this.paneWrapSize[1] - this.nodeBaseTop - (this.panePadding[0] + this.panePadding[2])
        boxAreasHeight -= this.nodesDataArray[0].nodesArray.length * this.nodeBottomMargin

        firstNodeHeight = Math.max(Math.round(boxAreasHeight * (firstNodeSessionCount / firstSequenceSessionCount)), this.firstNodeMinHeight)
        let initialNodeSize = (firstNodeHeight - this.firstNodeMinHeight) / (this.firstNodeMaxHeight - this.firstNodeMinHeight)
        initialNodeSize = this.sizeControlMin + ((this.sizeControlMax - this.sizeControlMin) * initialNodeSize)
        initialNodeSize = Math.round(initialNodeSize)
        if (this.nodeControlHeight !== initialNodeSize) {
          this.$emit('onChangeInitialNodeSize', Math.round(initialNodeSize))
          return
        }
        this.isInitialized = true
      }

      firstNodeHeight = this.firstNodeMinHeight + (this.firstNodeMaxHeight - this.firstNodeMinHeight) * (this.nodeControlHeight / (this.sizeControlMax - this.sizeControlMin))

      let listHeight = this.listHeight
      const numSequences = this.nodesDataArray.length
      for (let sequenceIndex = fromIndex || 0; sequenceIndex < numSequences; sequenceIndex++) {
        const sequence = this.nodesDataArray[sequenceIndex]
        const nodeLeft = sequenceIndex * (this.nodeWidth + this.nodeGap)
        let nodeTop = 0
        sequence.nodesArray.forEach((nodeData, nodeIndex) => {
          let height
          if (sequenceIndex === 0 && nodeIndex === 0) {
            height = firstNodeHeight
          } else {
            height = Math.max(Math.ceil(firstNodeHeight * (nodeData.sessCnt / firstNodeSessionCount)), this.nodeMinHeight)
          }
          height = height > 20 ? Math.ceil(height / 2) * 2 : height
          nodeData._layout = {
            left: nodeLeft,
            top: nodeTop,
            width: this.nodeWidth,
            height: height
          }
          nodeData.resampleSessCnt = nodeData.sessCnt / this.originTrendData.sessionSamplingRate
          nodeTop += height + this.nodeBottomMargin
        })
        listHeight = Math.max(listHeight, this.nodeBaseTop + nodeTop)
      }
      this.listHeight = listHeight

      this.calculateLineInfo(firstNodeHeight, firstNodeSessionCount, this.nodesDataArray, fromIndex)

      if (this.thruDataArray) {
        this.calculateLineInfo(firstNodeHeight, firstNodeSessionCount, this.thruDataArray)
      }

      requestAnimationFrame(() => {
        this.$emit('onNodeListResize')
      })
    },
    calculateLineInfo (firstNodeHeight, firstNodeSessionCount, targetDataArray, fromIndex) {
      const isThruData = targetDataArray === this.thruDataArray

      if (isThruData) {
        this.nodesDataArray.forEach((sequence, sequenceIndex) => {
          sequence.nodesArray.forEach((nodeData, nodeIndex) => {
            if (targetDataArray[sequenceIndex]) {
              const thruNode = targetDataArray[sequenceIndex].nodes[nodeData._id]
              if (thruNode) {
                thruNode._layout = nodeData._layout
                thruNode._realOutSessCnt = nodeData._realOutSessCnt
                thruNode._realInSessCnt = nodeData._realInSessCnt
              }
            }
          })
        })
      }

      const numSequences = targetDataArray.length
      for (let sequenceIndex = fromIndex || 0; sequenceIndex < numSequences; sequenceIndex++) {
        const sequence = targetDataArray[sequenceIndex]
        if (sequence && sequence.nodesArray) {
          const nextSequenceIndex = sequenceIndex + 1
          const nextSequenceData = targetDataArray[nextSequenceIndex]
          if (nextSequenceData) {
            sequence.nodesArray.forEach((nodeData, nodeIndex) => {
              nodeData.nextArray && nodeData.nextArray.forEach((nextData, nextIndex) => {
                const nextNodeData = nextSequenceData.nodes[nextData._id]
                if (nextNodeData && nextNodeData._layout) {
                  nextNodeData._enterY = nextNodeData._layout.top + 0.5
                }
              })
            })
            sequence.nodesArray.forEach((nodeData, nodeIndex) => {
              if (nodeData._layout) {
                let fromY = nodeData._layout.top + 0.5
                nodeData.nextArray && nodeData.nextArray.forEach((nextData, nextIndex) => {
                  const nextNodeData = nextSequenceData.nodes[nextData._id]
                  if (nextNodeData && nextNodeData._layout) {
                    let lineWidthOut = nodeData._layout.height * (nextData.sessCnt / nodeData._realOutSessCnt)
                    let lineWidthIn = nextNodeData._layout.height * (nextData.sessCnt / nextNodeData._realInSessCnt)
                    nextData._lineInfo = {
                      fromX: nextSequenceIndex * (this.nodeWidth + this.nodeGap) - this.nodeGap,
                      fromY: fromY,
                      toX: nextSequenceIndex * (this.nodeWidth + this.nodeGap),
                      toY: nextNodeData._enterY,
                      widthOut: Math.max(0, lineWidthOut - 1),
                      widthIn: Math.max(0, lineWidthIn - 1)
                    }
                    if (isThruData && this.nodesDataArray[sequenceIndex].nodes[nodeData._id].next[nextData._id]) {
                      const baseLineInfo = this.nodesDataArray[sequenceIndex].nodes[nodeData._id].next[nextData._id]._lineInfo
                      nextData._lineInfo.fromY = baseLineInfo.fromY
                      nextData._lineInfo.toY = baseLineInfo.toY
                      nextData._lineInfo.widthOut = Math.min(nextData._lineInfo.widthOut, baseLineInfo.widthOut)
                      nextData._lineInfo.widthIn = Math.min(nextData._lineInfo.widthIn, baseLineInfo.widthIn)
                      lineWidthOut = baseLineInfo.lineWidthOut
                      lineWidthIn = baseLineInfo.lineWidthIn
                    }
                    fromY += lineWidthOut
                    nextNodeData._enterY += lineWidthIn
                  }
                })
              }
            })
          }
        }
      }
    },
    adjustEventPoint (point) {
      const nodeList = this.$el
      const rect = nodeList.getBoundingClientRect()
      this._prevEventPoint = point
      return [point[0] - rect.left, point[1] - rect.top]
    },
    getEventPoint (e) {
      if (e.touches) {
        e = e.touches[0] || e.changedTouches[0]
      }
      return [e.pageX || e.clientX, e.pageY || e.clientY]
    },
    isSameEventPoint (e) {
      const point = this.getEventPoint(e)
      return this._prevEventPoint && this._prevEventPoint[0] === point[0] && this._prevEventPoint[1] === point[1]
    },
    getHighLow (seq) {
      const users = this.nodesDataArray[seq]
      const benchmark = this.benchmarkData[seq]
      const result = ((users.dropCnt / users.sampleSeqSessCnt) * 100).toFixed(2) - (benchmark * 100).toFixed(2)
      return result > 0 ? 'high' : result ? 'low' : 'equal'
    },
    setTracking (type) {
      const data = {
        init: this.tracking && this.tracking.type !== type,
        type: type === 'member' ? 'guest' : 'member',
        isAllSelected: true,
        isFromFunnel: true,
        selectedMembers: [],
        excludedMembers: []
      }

      if (this.tracking && this.tracking.type === type && !this.isTrackingAll) {
        data.type = type
      }

      this.$emit('setTracking', data)
    }
  }
}
</script>

<style lang="scss" scoped>
.node-list {
  position: relative;
  white-space: nowrap;

  &.thru-highlighting {
    & /deep/ {
      .node.unfocused .link {
        pointer-events: none;
      }
    }
  }

  .trend-paths {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;

    .path-group {
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      path {
        fill: #b8c008;
        stroke: #b8c008;
        stroke-width: 1;
        cursor: pointer;
        transition: fill 0.3s ease, opacity 0.3s ease-out;
      }
      path.default-path {
        opacity: 0.4;
      }
      &:hover path.default-path,
      &.active path.default-path {
        opacity: 1;
      }
      path.hoverable-path {
        fill: none !important;
        stroke: transparent !important;
        stroke-width: 5;
      }
      path.thru-path {
      }

      &:hover,
      &.active {
        path {
          fill: #75b3fe;
          stroke: #75b3fe;
        }
      }

      &.interrupted-group {
        mask: url(#interrupted-line-linear-gradient-mask);
        clip-path: url(#interrupted-line-linear-gradient-clip-path);
        &.active {
          mask: none;
          clip-path: none;
        }
      }
    }
  }
  &.thru-highlighting .trend-paths {
    .path-group {
      & /deep/ {
        path.default-path {
          opacity: calc(0.4 * 0.45);
        }
        &:hover path.default-path,
        &.active path.default-path {
          opacity: 0.4;
        }
      }
    }
  }

  .trend-path-defs {
    position: absolute;
    left: -999em;
    right: 0;
    top: 0;
    bottom: 0;
    width: 0;
    height: 0;
  }

  .column {
    width: 200px;
    vertical-align: top;
    display: inline-block;

    .more {
      position: absolute;
      top: -4px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      &-x {
        display: flex;
        background-color: #fff;
        padding: 10px;
        border-radius: 100%;
        box-shadow: 0px 5px 6px rgba(0, 0, 0, 0.16), 0px 0px 0px 1px rgba(0, 0, 0, 0.08);
        &:hover {
          background-color: #F4F4F4;
        }
      }
      &-text {
        color: #0E90A5;
        font-size: 12px;
        font-weight: 500;
        line-height: 1;
      }
    }

    & + .column {
      margin-left: 130px;
    }

    .col-info-header {
      position: absolute;
      top: -38px;
      transform: translateY(-100%);
      display: flex;
      align-items: center;
      gap: 10px;
      .match-type {
        padding: 4.5px 8px;
        border-radius: 25px;
        background-color: #2C7899;
        color: #fff;
        font-size: 13px;
        font-weight: 500;
        text-transform: capitalize;
        &.exact {
          background: #005BAD;
        }
      }
      .custom-button {
        display: flex;
        gap: 8px;
        align-items: center;
        flex-direction: row;
      }
      .category-header {
        position: relative;
        width: 100%;
        display: flex;
        align-items: center;
        margin-left: 11px;

        color: #444;
        font-size: 14px;
        font-weight: 700;
        line-height: 1.2;
        &::after {
          content: '';
          position: absolute;
          left: -10px;
          top: 50%;
          transform: translateY(-50%);
          width: 1px;
          height: 16px;
          background: #D1D1D1;
        }
        .category-icon {
          margin-right: 8px;
        }

        .dropdown {
          position: relative;
          cursor: pointer;
          margin-right: 12px;
          .selected {
            display: flex;
            align-items: center;
            gap: 12px;
            border-radius: 6px;
            border: solid 1px #bbb;
            background-color: #ffffff;
            padding: 8px 8px 8px 10px;
            cursor: pointer;
            .label {
              width: 114px;
              font-size: 14px;
              font-weight: 600;
              line-height: 1;
              color: #0185FF;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            }
            .icon {
              flex-shrink: 0;
            }
          }
        }
        .ai-robot-burrow {
          width: 41px;
          height: 41px;
          margin-left: 15px;
          transform: translateY(-5px);
          background: url('../../assets/img/dashboard/ai-robot-burrow.svg') no-repeat center;
          background-size: container;
          &.invert-h {
            transform: translateY(-5px) scaleX(-1);
            margin-left: 0;
            margin-right: 15px;
          }
        }
      }
    }

    .info {
      position: absolute;

      .sequence {
        position: absolute;
        right: 0;
        top: -7px;
        width: 60px;
        font-size: 50px;
        font-weight: 500;
        color: #555;
        letter-spacing: -1.5px;
        text-align: center;
        line-height: 1;

        &:after {
          content: 'SEQ';
          font-size: 11px;
          letter-spacing: 0;
          line-height: 13px;
          margin-top: -2px;
          display: block;
        }
      }

      .session,
      .dropoff {
        position: relative;
        height: 20px;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        color: #0e90a5;
        margin-left: 5px;
        padding-left: 26px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        gap: 4px;

        > .icon {
          position: absolute;
          left: 0;
          top: 0;
        }
        span {
          display: inline-block;
          &:nth-child(3) {
            color: #444;
          }
          &:nth-child(4) {
            position: relative;
            cursor: pointer;
            font-size: 12px;
            color: #0185FF;
          }
        }
        &.invalid-value {
          color: #444;
        }
      }
      span.tooltip-category {
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        background-color: #fff;
        color: #0185FF;
        font-size: 12px;
        font-weight: 400;
        line-height: 144%;
        display: flex;
        gap: 5px;
        padding: 6px;
        align-items: center;
        border-radius: 6px;
        margin-top: 8px;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.12);
        i {
          font-size: 14px;
          color: #0185FF;
        }
        &::after {
          content: '';
          position: absolute;
          left: 50%;
          width: 8px;
          height: 8px;
          /* clip-path: polygon(0 0, 100% 0, 50% 100%); */
          background-color: inherit;
          margin: -1px;
        }
        &.top::after {
          top: 100%;
          transform: translate(-50%, -50%) rotate(-45deg);
          box-shadow: -1px 1px 0 0 rgba(0, 0, 0, 0.16);
        }
        &.bottom::after {
          bottom: 100%;
          transform: translate(-50%, 50%) rotate(-45deg);
          box-shadow: 1px -1px 0 0 rgba(0, 0, 0, 0.16);
        }
        &.no-category {
          bottom: 100%;
          top: auto;
          margin-bottom: 16px;
          &:lang(en) {
            left: 0;
            transform: translateX(0);
            &:after {
              left: 100px;
            }
          }
        }
      }
      .dropoff {
        font-size: 15px;
        font-weight: 500;
        color: #ff4a00;
        margin-top: 8px;
        padding-top: 1px;
      }

      &:after {
        height: 3px;
        content: '';
        margin: 12px 5px 0;
        border-radius: 1.5px;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
        background-color: #fff;
        display: block;
      }

      /deep/ .tracking-funnel {
        position: absolute;
        top: calc(50% - 6px);
        left: -34px;
        transform: translate(-100%, -50%);
      }
      .trend-ai-referrer-summary {
        position: absolute;
        top: 0;
        left: -40px;
        transform: translateX(-100%);

        &.is-below-tracking-funnel {
          top: auto;
          bottom: -90px;
          transform: translate(-100%, 100%);
        }
      }
      .tracking-label {
        position: absolute;
        top: calc(50% - 6px);
        left: -48px;
        transform: translateY(-50%);
      }
    }
  }
}

.dimmer {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  content: '';
  background-color: rgba(244, 244, 241, 0.55);
  z-index: 3;
  pointer-events: none;

  &.thru {
    top: 135px;
  }
}

.fade {
  &-leave-active,
  &-enter-active {
    transition: opacity 0.3s ease-out;
    opacity: 1;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }
}
</style>
